import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Headroom from 'headroom.js'
import {
  Badge,
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Media,
  Nav,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
  Row,
  UncontrolledCollapse,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap'
import { useUser } from 'context/UserContext'
import { FaCircleUser } from 'react-icons/fa6'
import { FaFlag } from 'react-icons/fa'

const NavbarComponent : React.FC = () => {
  const { user, logout } = useUser()
  const [collapseClasses, setCollapseClasses] = useState('')
  const [totalUsers, setTotalUsers] = useState<number | null>(null)
  const [totalReports, setTotalReports] = useState<number | null>(null)
 
  const handleLogout = () => {
    logout()
    localStorage.removeItem('token')
  }
 
  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('token')
      const headers : Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ token }`,
      }
   
      const response = await fetch('/api/admin/navbar-data', { headers })
   
      if (!response.ok) {
        throw new Error('Failed to submit report')
      }
   
      if (response.ok) {
        const data = await response.json()
        setTotalUsers(data.totalUsers)
        setTotalReports(data.totalReports)
      } else {
        throw new Error('Failed to fetch navbar data')
      }
    } catch (error) {
      console.error('Error fetching admin data:', error)
    }
  }
 
  useEffect(() => {
    if (user?.roleId === 1) {
      fetchAdminData()
    }
  }, [user])
 
  useEffect(() => {
    const navbar = document.getElementById('navbar-main')
    if (navbar) {
      const headroom = new Headroom(navbar)
      headroom.init()
    }
  }, [])
 
  const onExiting = () => {
    setCollapseClasses('collapsing-out')
  }
 
  const onExited = () => {
    setCollapseClasses('')
  }
 
  return (
    <>
      <header className="header-global">
        <Navbar
          className="navbar-main navbar-transparent navbar-dark headroom"
          expand="lg"
          id="navbar-main"
        >
          <Container style={ { padding: 'inherit' } }>
            <NavbarBrand className="mr-lg-5" to="/">
              <img
                alt="..."
                src="/img/icon.png"
              />
            </NavbarBrand>
            <button className="navbar-toggler" id="navbar_global">
              <span className="navbar-toggler-icon"/>
            </button>
            <UncontrolledCollapse
              toggler="#navbar_global"
              navbar
              className={ collapseClasses }
              onExiting={ onExiting }
              onExited={ onExited }
            >
              <div className="navbar-collapse-header">
                <Row>
                  <Col className="collapse-brand" xs="6">
                    <Link to="/">
                      <img
                        alt="..."
                        src="/img/icon.png"
                      />
                    </Link>
                  </Col>
                  <Col className="collapse-close" xs="6">
                    <button className="navbar-toggler" id="navbar_global">
                      <span/>
                      <span/>
                    </button>
                  </Col>
                </Row>
              </div>
              <Nav className="navbar-nav-hover align-items-lg-center text-black" navbar>
                <NavItem>
                  <NavLink
                    className="nav-link"
                    href="/"
                    id="tooltipHome"
                  >
                    <span className="nav-link-inner--text text-black">
                        Home
                    </span>
                  </NavLink>
                  <UncontrolledTooltip delay={ 0 } target="tooltipHome">
                    Navigate to Home page
                  </UncontrolledTooltip>
                </NavItem>
        
                { user && (
                  <>
                    <NavItem>
                      <NavLink
                        className="nav-link"
                        href="/gallery"
                        id="tooltipGallery"
                      >
                        <span className="nav-link-inner--text text-black">
                        Gallery
                        </span>
                      </NavLink>
                      <UncontrolledTooltip delay={ 0 } target="tooltipGallery">
                        Show your gallery page
                      </UncontrolledTooltip>
                    </NavItem>
                  </>
                ) }
        
                { user?.roleId === 1 && (
                  <Nav className="navbar-nav-hover align-items-lg-center" navbar>
                    <UncontrolledDropdown nav>
                      <DropdownToggle nav>
                        <i className="ni ni-ui-04 d-lg-none mr-1"/>
                        <span className="nav-link-inner--text text-black">Administration</span>
                      </DropdownToggle>
                      <DropdownMenu className="dropdown-menu-xl">
                        <div className="dropdown-menu-inner">
                          <Media
                            className="d-flex align-items-center"
                            href="/admin/home"
                          >
                            <div className="icon icon-shape bg-gradient-primary rounded-circle">
                              <i className="ni ni-spaceship"/>
                            </div>
                            <Media body className="ml-3">
                              <h6 className="heading text-primary mb-md-1 text-black">
                                Admin home
                              </h6>
                            </Media>
                          </Media>
                          <Media className="d-flex align-items-center" href="/admin/users">
                            <div className="icon icon-shape bg-gradient-cyan rounded-circle">
                              <FaCircleUser/>
                            </div>
                            <Media body className="ml-3">
                              <h6 className="heading text-primary mb-md-1 text-black">
                                Users list
                              </h6>
                              <Badge color="primary" pill>
                                Total <Badge className="badge-white">{ totalUsers ?? '?' }</Badge>
                              </Badge>
                            </Media>
                          </Media>
                          <Media
                            className="d-flex align-items-center"
                            href="/admin/reports"
                          >
                            <div className="icon icon-shape bg-gradient-warning rounded-circle">
                              <FaFlag/>
                            </div>
                            <Media body className="ml-3">
                              <h6 className="heading text-warning mb-md-1 text-black">
                                Reports list
                              </h6>
                              <Badge color="warning" pill>
                                Total <Badge className="badge-white">{ totalReports ?? '?' }</Badge>
                              </Badge>
                            </Media>
                          </Media>
                        </div>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </Nav>
                ) }
              </Nav>
              <Nav className="align-items-lg-center ml-lg-auto" navbar>
                <Button className="btn-icon btn-3" color="primary" type="button"
                  href="https://github.com/Sorok-Dva/ScreenMe/releases/download/1.2.0/ScreenMe.exe"
                  target="_blank">
                  <span className="btn-inner--icon">
                    <i className="fa fa-cloud-download mr-2"/>
                  </span>
                  <span className="btn-inner--text">Download</span>
                </Button>
                <UncontrolledDropdown>
                  <DropdownToggle caret color="info">
                    <FaCircleUser/> { user ? user.nickname: '' }
                  </DropdownToggle>
                  <DropdownMenu>
                    { user ? (
                      <>
                        <DropdownItem href={`/user/${user.nickname}`}>
                          My profile
                        </DropdownItem>
                        <DropdownItem href="/settings">
                          Settings
                        </DropdownItem>
                        <DropdownItem divider/>
                        <DropdownItem href="/" onClick={ handleLogout }>
                          Logout
                        </DropdownItem>
                      </>
                    ): (
                      <>
                        <DropdownItem href="/login">
                          Login
                        </DropdownItem>
                        <DropdownItem href="/register">
                          Register
                        </DropdownItem>
                      </>
                    ) }
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Nav>
            </UncontrolledCollapse>
          </Container>
        </Navbar>
      </header>
    </>
  )
}

export default NavbarComponent
